'use client'

import { useState } from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { TOPUP_CONFIG, INITIAL_FREE_PLAN_CREDITS } from '@/lib/utils'
import { Tabs, TabsList, TabsTrigger } from '@/components/animate-ui/components/tabs'
import PricingCard from '@/components/ui/pricing-card'
import { useAuthMinimal } from '@/hooks/useAuthMinimal'
import { useBillingMinimal } from '@/hooks/useBillingMinimal'

interface PlanFeature {
  text: string
  hasTooltip?: boolean
  tooltipText?: string
}

interface PricingPlan {
  id: string
  name: string
  monthlyPrice: number
  yearlyPrice: number
  monthlyLookupKey?: string
  yearlyLookupKey?: string
  emoji: string
  isPopular?: boolean
  buttonText: string
  buttonVariant?: 'default' | 'outline'
  features: PlanFeature[]
  includes?: string
  disclaimer?: string
  disclaimerTooltip?: string
  monthlyCredits?: number
  useCaseLabel?: string
}

interface PricingCardsContainerProps {
  mode: 'light' | 'dark'
  pricingPlans: PricingPlan[]
  payAsYouGoPlan?: PricingPlan
}

export function PricingCardsContainer({ pricingPlans, payAsYouGoPlan }: PricingCardsContainerProps) {
  const [activeTab, setActiveTab] = useState<'monthly' | 'yearly'>('monthly')
  const starter = pricingPlans.find(plan => plan.id === 'starter')
  const isYearly = activeTab === 'yearly'
 
  const { user, loading: authLoading } = useAuthMinimal()
  const { billingData, loading: billingLoading } = useBillingMinimal(user)

  /* Check if loading is complete (either no user or billing data loaded) */
  const isLoadingComplete = !authLoading && (!user || !billingLoading)

  return (
    <>
      <div className="mt-6 w-full rounded-2xl border bg-background p-6 md:p-8">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="space-y-2">
            <h3 className="max-w-2xl text-2xl font-medium">{INITIAL_FREE_PLAN_CREDITS} free credits to start.</h3>
            <p className="text-muted-foreground">All features and AI models with purchased credits. No subscription required.</p>
          </div>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6 md:shrink-0">
            <p className="whitespace-nowrap text-3xl font-medium">${TOPUP_CONFIG.PRICE_PER_CREDIT.toFixed(2)} <span className="text-sm text-muted-foreground">/ credit</span></p>
            <Button asChild size="lg" className="h-14 w-full rounded-xl px-8 text-base sm:w-auto"><Link href={user ? '/dashboard/billing#buy-credits' : `/signup?redirect=${encodeURIComponent('/dashboard/billing#buy-credits')}`}>{user ? 'Buy credits' : 'Start free'}</Link></Button>
          </div>
        </div>
      </div>
      <div className="mt-8 w-full space-y-2">
        <h3 className="text-2xl font-medium">Subscriptions are optional. Get more for less.</h3>
        <p className="text-muted-foreground">{starter?.monthlyCredits?.toLocaleString()} credits for ${starter?.monthlyPrice.toFixed(2)}/month with Starter, or ${(starter?.monthlyCredits ?? 0) * TOPUP_CONFIG.PRICE_PER_CREDIT} pay as you go.</p>
      </div>
      <div className="my-4 flex w-full justify-center">
        <div className="flex w-full flex-col items-center justify-center gap-3 md:w-60">
          <Tabs
            value={activeTab}
            onValueChange={(value: 'monthly' | 'yearly') => setActiveTab(value)}
            className="w-full"
          >
            <TabsList className="w-full">
              <TabsTrigger
                value="monthly"
                className="flex-1"
              >
                Monthly
              </TabsTrigger>
              <TabsTrigger
                value="yearly"
                className="flex-1"
              >
                Yearly
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>
      
      <div className="mt-7 flex w-full flex-col gap-6 rounded-3xl lg:flex-row lg:items-stretch lg:gap-0 lg:border lg:bg-background">
        {payAsYouGoPlan && (
          <PricingCard
            key={payAsYouGoPlan.id}
            {...payAsYouGoPlan}
            isYearly={isYearly}
            user={user}
            billingData={billingData}
            isLoadingComplete={isLoadingComplete}
          />
        )}
        {pricingPlans.map((plan) => (
            <PricingCard
              key={plan.id}
              {...plan}
              isYearly={isYearly}
              user={user}
              billingData={billingData}
              isLoadingComplete={isLoadingComplete}
              lookupKey={isYearly ? plan.yearlyLookupKey : plan.monthlyLookupKey}
            />
        ))}
      </div>
    </>
  )
}
